import { useEffect, useState } from "react";
import { Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { ACCOUNT_TYPES } from "@/lib/translations";
import { Field, NativeSelect, PageHeader, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPE_COLOR = { asset: "bg-[#007A3D]", liability: "bg-[#CE1126]", equity: "bg-slate-900", revenue: "bg-emerald-400", expense: "bg-[#D4AF37]" };

function AccountDialog({ open, onClose, onSaved }) {
  const { t } = useLang();
  const [f, setF] = useState({ code: "", name: "", type: "expense" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/accounts", f); toast.success(t("saved")); setF({ code: "", name: "", type: "expense" }); onSaved(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="account-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("newAccount")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-3 gap-4">
          <Field label={t("code")}><Input data-testid="account-code-input" className="font-mono" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} required /></Field>
          <Field label={t("name")} className="col-span-2"><Input data-testid="account-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
          <Field label={t("type")} className="col-span-3">
            <NativeSelect data-testid="account-type-select" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
              {ACCOUNT_TYPES.map((x) => <option key={x} value={x}>{t(`type_${x}`)}</option>)}
            </NativeSelect>
          </Field>
          <div className="col-span-3 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t("cancel")}</Button>
            <Button data-testid="account-save-btn" className="bg-[#007A3D] hover:bg-[#005E2E]">{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Accounts() {
  const { t, accName } = useLang();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/accounts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (a) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/accounts/${a.id}`); toast.success(t("deleted")); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="accounts-page">
      <PageHeader title={t("accounts")} subtitle={t("accountsSubtitle")}
        actions={<Button data-testid="new-account-btn" onClick={() => setOpen(true)} className="bg-[#007A3D] hover:bg-[#005E2E]"><Plus className="me-1 h-4 w-4" />{t("newAccount")}</Button>} />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {ACCOUNT_TYPES.map((type) => (
          <Panel key={type} className="overflow-hidden" data-testid={`accounts-group-${type}`}>
            <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-3">
              <span className={`h-2.5 w-2.5 rounded-sm ${TYPE_COLOR[type]}`} />
              <h3 className="font-display font-bold">{t(`type_${type}`)}</h3>
            </div>
            <table className="data-table">
              <tbody>
                {rows.filter((a) => a.type === type).map((a) => (
                  <tr key={a.id} data-testid={`account-row-${a.code}`}>
                    <td className="w-20 font-mono font-semibold text-slate-900">{a.code}</td>
                    <td>{accName(a)}</td>
                    <td className="w-16 text-end">
                      {a.system ? <Lock className="ms-auto h-3.5 w-3.5 text-slate-300" title={t("systemAccount")} /> :
                        <button data-testid={`delete-account-${a.code}`} onClick={() => remove(a)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-[#CE1126]"><Trash2 className="h-4 w-4" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        ))}
      </div>
      <AccountDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
